import { Box, Button, Card, CardContent, InputLabel, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseHeaderTitle, BaseRow } from '~/components/common';
import styles from './RingCentral.module.scss';
import classNames from 'classnames/bind';
import { useContext, useState } from 'react';
import RingCentralConnectButton from '~/components/ring-central/RingCentralConnectButton';
import { ConnectionStatusEnum } from '~/utils/enum';
import { RingCentralContext } from '~/context/RingCentralContext';
import { useFormik } from 'formik';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useAppDispatch } from '~/redux/hooks';
import { IRingCentral } from '~/utils/interface/ring-central';
import { handleUpdateRingCentral } from '~/thunks/ring-central/ringCentralThunk';
import { PERMISSIONS } from '~/utils/constants/permission';
import usePermission from '~/hooks/usePermission';

const cx = classNames.bind(styles);
const initialValues: IRingCentral = {};

const RingCentral = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/ring-central']);
  const { ringCentralStatus } = useContext(RingCentralContext);
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const form = useFormik({
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(handleUpdateRingCentral(values)).unwrap();
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        handleEditSetting(false, true);
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isEditSetting, setIsEditSetting] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook

  //#endregion Implement Hook

  //#region Handle Function
  const handleEditSetting = (isEditSetting: boolean, needRefreshData: boolean = false) => {
    setIsEditSetting(isEditSetting);
    if (!isEditSetting) {
      form.resetForm();
    }
  };
  //#endregion Handle Function

  return (
    <>
      <BaseHeaderTitle className='mb-[20px]' title={t('ringCentral')} />
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography className='mb-[20px]' variant='h5'>
            {t('settings.title')}
          </Typography>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('settings.clientId')}</InputLabel>
              <TextField
                name='clientId'
                placeholder='********'
                value={cast(form.values?.clientId)}
                onChange={form.handleChange}
                disabled={!isEditSetting}
                error={Boolean(form.errors.clientId)}
                helperText={form.errors.clientId}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('settings.clientSecret')}</InputLabel>
              <TextField
                name='clientSecret'
                placeholder='********'
                value={cast(form.values?.clientSecret)}
                onChange={form.handleChange}
                disabled={!isEditSetting}
                error={Boolean(form.errors.clientSecret)}
                helperText={form.errors.clientSecret}
              />
            </Box>
          </BaseRow>
          {permissions[PERMISSIONS.ringCentral_connect] && (
            <BaseRow className='justify-end gap-[10px]'>
              {!isEditSetting && (
                <Button color='primary' variant='contained' onClick={() => handleEditSetting(true)}>
                  {t('edit', { ns: 'admin/common' })}
                </Button>
              )}
              {isEditSetting && (
                <>
                  <Button color='secondary' variant='contained' onClick={() => handleEditSetting(false)}>
                    {t('cancel', { ns: 'admin/common' })}
                  </Button>
                  <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
                    {t('save', { ns: 'admin/common' })}
                  </Button>
                </>
              )}
            </BaseRow>
          )}
        </CardContent>
      </Card>
      <BaseRow className='gap-[20px] mb-[30px]'>
        <Card className='w-full max-w-[500px]'>
          <CardContent className='h-[200px] flex flex-col justify-between items-center'>
            <Typography variant='h5'>{t('ringCentralStatus')}</Typography>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box
                className={cx('ring-central-status', {
                  connected: ringCentralStatus === ConnectionStatusEnum.CONNECTED,
                  connecting: ringCentralStatus === ConnectionStatusEnum.CONNECTING,
                })}
              >
                {t(`isRingCentralConnected.${ringCentralStatus}`)}
              </Box>
            </BaseRow>
            <BaseRow>
              <RingCentralConnectButton />
            </BaseRow>
          </CardContent>
        </Card>
      </BaseRow>
    </>
  );
};

export default RingCentral;
